import { ReactNode, useEffect, useRef } from 'react';
import {
    BreakpointRangeProps,
    Container,
    Dialog,
    SelectionCardGroup,
    Typography,
    withBreakpointRange,
} from '@optimus-web/core';
import { getTemplateColumnsGridSize } from '../utils/getTemplateColumnsGridSize';
import { getCurrencyOptions } from './utils/getCurrencyOptions';
import { getSelectionCardGroupOptions } from './utils/getSelectionCardGroupOptions';

export interface Currency {
    code: string;
    symbol: string;
    description: string | null;
}

interface CurrencySelectDialogProps extends BreakpointRangeProps {
    currencies: Currency[];
    isActive: boolean;
    onChange: (currencyCode: string) => void;
    onClose: () => void;
    portalContainerId: string;
    propertyCurrencyCode?: string;
    selectedCurrencyCode: string;
    suggestedCurrencyCodes?: string[];
    dialogTitleText: ReactNode;
    suggestedCurrenciesTitleText?: string;
    otherCurrenciesTitleText: string;
    propertyCurrencyLabelText?: string;
}

const BaseCurrencySelectDialog = ({
    breakpointRange,
    currencies,
    isActive,
    onChange,
    onClose,
    portalContainerId,
    propertyCurrencyCode,
    selectedCurrencyCode,
    suggestedCurrencyCodes = [],
    dialogTitleText,
    suggestedCurrenciesTitleText,
    otherCurrenciesTitleText,
    propertyCurrencyLabelText,
}: CurrencySelectDialogProps) => {
    const dialogContentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isActive) {
            window.setTimeout(() => {
                const firstCheckboxEl = dialogContentRef.current?.querySelector('button[role="checkbox"]');
                if (firstCheckboxEl instanceof HTMLButtonElement) firstCheckboxEl.focus();
            }, 150);
        }
    }, [isActive]);

    const { suggestedCurrencyOptions, otherCurrencyOptions } = getCurrencyOptions({
        currencies,
        selectedCurrencyCode,
        suggestedCurrencyCodes,
    });

    const suggestedCurrenciesCardGroupOptions = getSelectionCardGroupOptions({
        currencies: suggestedCurrencyOptions,
        propertyCurrencyCode,
        propertyCurrencyLabelText,
    });

    const otherCurrenciesCardGroupOptions = getSelectionCardGroupOptions({
        currencies: otherCurrencyOptions,
        propertyCurrencyCode,
        propertyCurrencyLabelText,
    });

    const hasSuggestedCurrenciesCardGroupOptions = suggestedCurrenciesCardGroupOptions.length > 0;

    const handleChange = (currencyCode: string | undefined) => {
        // For SelectionCardGroup we are using type="checkbox" so we need to prevent the user from unselecting the value
        if (currencyCode !== undefined) {
            onChange(currencyCode);
        }
    };

    return (
        <Dialog
            active={isActive}
            modal
            onClose={onClose}
            size="extraLarge"
            title={dialogTitleText}
            portalContainerId={portalContainerId}
            contentRef={dialogContentRef}
        >
            <Container
                maxHeight="600px"
                padding={hasSuggestedCurrenciesCardGroupOptions ? ['0', '0', '100'] : ['50', '0']}
            >
                {hasSuggestedCurrenciesCardGroupOptions && (
                    <>
                        <Container padding={['0', '0', '300']}>
                            <Typography textStyle="titleSmallStrong" as="div">
                                {suggestedCurrenciesTitleText}
                            </Typography>
                        </Container>

                        <SelectionCardGroup
                            inputValue={[]}
                            onChange={(e_, newValue) => handleChange(newValue[0])}
                            options={suggestedCurrenciesCardGroupOptions}
                            showSelector={false}
                            templateColumns={getTemplateColumnsGridSize(breakpointRange)}
                            type="checkbox"
                        />

                        <Container padding={['300', '0']}>
                            <Typography textStyle="titleSmallStrong" as="div">
                                {otherCurrenciesTitleText}
                            </Typography>
                        </Container>
                    </>
                )}
                <SelectionCardGroup
                    inputValue={[selectedCurrencyCode]}
                    onChange={(e_, newValue) => handleChange(newValue[1])}
                    options={otherCurrenciesCardGroupOptions}
                    showSelector={false}
                    templateColumns={getTemplateColumnsGridSize(breakpointRange)}
                    type="checkbox"
                />
            </Container>
        </Dialog>
    );
};

export const CurrencySelectDialog = withBreakpointRange<CurrencySelectDialogProps>(BaseCurrencySelectDialog);
